/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import {
    Component,
    Input,
    OnInit,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { IParsedFault } from 'ng/modules/virtual-service/utils/virtual-service-faults.utils';
import './avi-health-score-faults.component.less';
import * as l10n from './avi-health-score-faults.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Component for displaying health score faults.
 * @component AviHealthScoreFaultsComponent
 */
@Component({
    selector: 'avi-health-score-faults',
    templateUrl: './avi-health-score-faults.component.html',
})
export class AviHealthScoreFaultsComponent implements OnInit {
    /**
     * Array of faults to display.
     */
    @Input()
    public faults: IParsedFault[] = [];

    /**
     * Whether this is for a service engine.
     */
    @Input()
    public isServiceEngine = false;

    /**
     * Callback function for closing messages.
     */
    @Input()
    public onCloseMessage: () => void;

    /**
     * Used to toggle displaying the list of faults.
     */
    public showList = false;

    /**
     * Total count of faults.
     */
    public count = 0;

    /**
     * List of generic faults (non-server faults).
     */
    public genericFaults: any[] = [];

    /**
     * List of server faults.
     */
    public serverFaults: any = null;

    public readonly l10nKeys = l10nKeys;

    constructor(l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    public ngOnInit(): void {
        this.count = this.getCount();
        this.genericFaults = this.getGenericFaults();
        this.serverFaults = this.getServerFaults();
    }

    /**
     * Toggles the view between showing and hiding the list of faults.
     */
    public toggleList(): void {
        this.showList = !this.showList;
    }

    /**
     * TrackBy function for index.
     * @param index
     * @returns index
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Returns the count of total faults. If faults are of type 'server_fault', faults are
     * nested further.
     * @return {number}
     */
    private getCount(): number {
        let counter = 0;

        this.faults.forEach(fault => {
            if (fault.id === 'server_faults') {
                fault.faults.forEach((innerFault: any) => {
                    counter += innerFault.servers.length;
                });
            } else {
                counter += 1;
            }
        });

        return counter;
    }

    /**
     * Returns a list of generic faults.
     * @return {Object[]}
     */
    private getGenericFaults(): any[] {
        return this.faults.filter(fault => fault.id !== 'server_faults');
    }

    /**
     * Returns a list of server faults.
     * @return {Object}
     */
    private getServerFaults(): any {
        return this.faults.find(fault => fault.id === 'server_faults');
    }
}
